import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUser, createCheckoutSession } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import './Subscription.css';

const Subscription = () => {
  const { user: authUser, loading: authLoading } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for success or canceled query parameters
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('subscription') === 'success') {
      setSuccess(true);
    }
    if (queryParams.get('canceled') === 'true') {
      setError("Payment process was canceled. You can try again when you're ready.");
    }

    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userData = await getUser();
        setUser(userData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user information. Please try again later.');
        setLoading(false);
      }
    };

    fetchUserData();
  }, [location.search]);

  const handleUpgradeSubscription = async (planId) => {
    try {
      setProcessingPayment(true);
      setError(null);
      
      // Create a checkout session with Stripe
      const { sessionUrl } = await createCheckoutSession(planId);
      
      // Redirect to Stripe Checkout
      window.location.href = sessionUrl;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to initiate payment process. Please try again later.');
      setProcessingPayment(false);
    }
  };

  const handleGoBack = () => {
    navigate('/dashboard');
  };

  // If auth is still loading, show loading state
  if (authLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <h1>Loading subscription information...</h1>
          <div className="loading-spinner"></div>
          <p>Please wait while we prepare your subscription options</p>
          <div className="loading-progress"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-container">
      <Navbar />
      <div className="subscription-content">
        {success && (
          <div className="success-alert">
            <div className="success-alert-message">
              <h3>Subscription Upgraded Successfully!</h3>
              <p>Thank you for upgrading your subscription. Your new plan is now active and you can create more briefs.</p>
            </div>
            <button 
              className="success-alert-close" 
              onClick={() => setSuccess(false)}
            >
              ×
            </button>
          </div>
        )}

        <div className="subscription-header">
          <h1>Upgrade Your Subscription</h1>
          <p className="subscription-subtitle">
            Increase your active brief limit and unlock more features
          </p>
        </div>

        {error && (
          <div className="error-alert">
            <div className="error-alert-message">{error}</div>
            <button 
              className="error-alert-close" 
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}

        <div className="current-plan">
          <h2>Your Current Plan</h2>
          <div className="current-plan-details">
            <div className="plan-info">
              <span className="plan-label">Plan:</span>
              <span className="plan-value">{user?.subscription?.plan?.charAt(0).toUpperCase() + user?.subscription?.plan?.slice(1) || 'Free'}</span>
            </div>
            <div className="plan-info">
              <span className="plan-label">Active Brief Limit:</span>
              <span className="plan-value">{user?.subscription?.limits?.max_queries || 2}</span>
            </div>
            <div className="plan-info">
              <span className="plan-label">Status:</span>
              <span className={`subscription-status ${user?.subscription?.status || 'inactive'}`}>
                {user?.subscription?.status?.charAt(0).toUpperCase() + user?.subscription?.status?.slice(1) || 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        <div className="subscription-plans">
          <h2>Choose Your Plan</h2>
          
          <div className="plans-grid">
            <div className="plan-card">
              <div className="plan-card-header">
                <h3>Free</h3>
                <div className="plan-price">$0<span>/month</span></div>
              </div>
              <div className="plan-features">
                <ul>
                  <li>Up to 2 active briefs</li>
                  <li>Daily updates at your chosen time</li>
                  <li>Basic topic customization</li>
                  <li>Email delivery</li>
                </ul>
              </div>
              <button 
                className="plan-select-button"
                disabled={true}
              >
                Current Plan
              </button>
            </div>

            <div className="plan-card featured">
              <div className="plan-card-header">
                <div className="plan-badge">Premium</div>
                <h3>Standard</h3>
                <div className="plan-price">$2.99<span>/month</span></div>
              </div>
              <div className="plan-features">
                <ul>
                  <li>Up to 10 active briefs</li>
                  <li>Daily updates at your chosen time</li>
                  <li>Advanced topic customization</li>
                  <li>Customized queries</li>
                  <li>Email delivery</li>
                  <li>Priority support</li>
                </ul>
              </div>
              <button 
                className="plan-select-button"
                onClick={() => handleUpgradeSubscription('standard')}
                disabled={processingPayment || (user?.subscription?.plan === 'standard' && user?.subscription?.status === 'active')}
              >
                {processingPayment ? 'Processing...' : (user?.subscription?.plan === 'standard' && user?.subscription?.status === 'active') ? 'Current Plan' : 'Select Plan'}
              </button>
            </div>
          </div>
        </div>

        <div className="subscription-faq">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>What happens when I upgrade?</h3>
              <p>When you upgrade your subscription, your new plan will be activated immediately. You'll be able to create more active briefs right away and access all the features included in your new plan.</p>
            </div>
            <div className="faq-item">
              <h3>Can I downgrade later?</h3>
              <p>Yes, you can change your subscription at any time. If you downgrade, your plan will change at the end of your current billing cycle, and you'll need to reduce your active briefs to match your new plan's limit.</p>
            </div>
            <div className="faq-item">
              <h3>How is my brief limit enforced?</h3>
              <p>Your subscription determines how many briefs you can have active at once. You can create as many briefs as you want, but only a limited number can be active simultaneously. You can always disable some briefs to enable others.</p>
            </div>
            <div className="faq-item">
              <h3>How do I cancel my subscription?</h3>
              <p>You can cancel your subscription at any time from your account settings. After cancellation, your plan will remain active until the end of your current billing period.</p>
            </div>
          </div>
        </div>

        <div className="subscription-footer">
          <button className="back-button" onClick={handleGoBack}>
            Back to Dashboard
          </button>
          <div className="subscription-help">
            <p>Need help choosing a plan? <a href="mailto:support@dailyaibriefing.com">Contact our support team</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription; 